/**
 * Order Wizard — Multi-step cupcake ordering flow v2
 * ================================================
 * Step 1: Order Type (Pickup / Delivery)
 * Step 2: Date Selection (custom calendar)
 * Step 3: Box Size
 * Step 4: Build Your Box (flavor catalog + visual preview)
 *
 * Integrates with OrderScheduler for session persistence
 * and Shopify AJAX Cart API for cart submission.
 */
(function () {
  'use strict';

  /* ── CONFIG & DATA ────────────────────────────────────────── */
  var config, allFlavors, allBoxes, fillerBoxes;

  try {
    config     = JSON.parse(document.getElementById('ow-config').textContent);
    allFlavors = JSON.parse(document.getElementById('ow-flavor-data').textContent);
    allBoxes   = JSON.parse(document.getElementById('ow-box-data').textContent);
  } catch (e) {
    console.error('[OrderWizard] Failed to parse data:', e);
    return;
  }

  /* ── FILLER BOX DATA — custom-box decomposition only ─────────
     `fillerBoxes` is keyed by piece count ("1", "2", "4") and points at
     hidden "Custom Box Filler — N Cupcakes" twin products that mirror the
     real 1/2/4 Cupcakes SKUs but carry NO `meals-tax` tag. The wizard's
     decomposition path uses these for the remainder of any custom-quantity
     order so a custom box of 7 (= 6 + 1) doesn't accidentally collect
     Harrisonburg meals tax on the lone single-cupcake line item.

     Standalone direct purchases of 1/2/4 Cupcakes from the storefront
     still hit the originals (correctly tagged), so meals tax applies there.

     If the filler block is missing for any reason (bad theme deploy, etc.)
     we fall back to `allBoxes` so the wizard still works — customers just
     temporarily get the old (slightly over-taxing) behavior. */
  try {
    var fillerEl = document.getElementById('ow-filler-box-data');
    fillerBoxes = fillerEl ? JSON.parse(fillerEl.textContent) : {};
  } catch (e) {
    console.warn('[OrderWizard] Failed to parse filler box data; falling back to allBoxes for custom decomposition:', e);
    fillerBoxes = {};
  }

  /* ── Gluten-Friendly image fallback ──────────────────────────
     Several GF products in Shopify (e.g. "Carrot Gluten Friendly",
     "Lemon Drop Gluten Friendly") don't have their own images yet.
     Without this pass, those tiles render as blank squares in the
     box builder. Fall back to the non-GF counterpart's featured
     image — visually the cupcake looks the same, and the underlying
     title/handle stays intact so Square still receives the correct
     GF SKU via line item properties.

     One-liner to revisit once the GF products each have their own
     images uploaded in Shopify admin — the fallback becomes a no-op
     at that point (because f.image will already be truthy).

     IMPORTANT (Mike 2026-06-02): this is DEFINED here but CALLED after the
     synthetic flavors are merged into allFlavors (see below). Running it
     earlier missed synthetic-only GF tiles that have no Shopify product
     (e.g. "Vanilla Gluten Friendly"), leaving them blank in the box builder.
     BASE_ALIASES covers names whose stripped base isn't a 1:1 product title
     ("Vanilla" → "Vanilla Vanilla"). */
  function fillGfImagesFromBase() {
    var byTitle = {};
    allFlavors.forEach(function (f) {
      if (f.title && f.image) byTitle[f.title.toLowerCase()] = f.image;
    });
    var BASE_ALIASES = { 'vanilla': 'vanilla vanilla' }; // "Vanilla Gluten Friendly" → "Vanilla Vanilla" image
    allFlavors.forEach(function (f) {
      if (f.image) return;
      if (!f.title || !/\s+Gluten Friendly$/i.test(f.title)) return;
      var baseTitle = f.title.replace(/\s+Gluten Friendly$/i, '').trim().toLowerCase();
      if (byTitle[baseTitle]) { f.image = byTitle[baseTitle]; return; }
      if (BASE_ALIASES[baseTitle] && byTitle[BASE_ALIASES[baseTitle]]) f.image = byTitle[BASE_ALIASES[baseTitle]];
    });
  }

  /* ── WEEKLY SCHEDULE — pulled from the shared source of truth.
     Edit flavors in `assets/cupcake-schedule.js`; both the Daily Menu and
     this Box Builder pick up the change automatically. ── */
  var _schedule = (window.CupcakeSchedule) || {
    everyday: [], daily: {}, gfPerDay: {},
    normalize: function (s) { return String(s || '').toLowerCase(); },
    allFlavorNamesLower: function () { return []; }
  };
  var _norm = _schedule.normalize;

  /* ── Display-name helper ──
     Trims " Gluten Friendly" from the end of a flavor name for display.
     The GF badge already signals this, so showing it in the name is
     redundant. The internal title/handle/cart property stays as-is
     (e.g., "Red Velvet Gluten Friendly") so Square still receives the
     correct product identity via line item properties. */
  function displayTitle(title) {
    return String(title || '')
      .replace(/\s+Gluten Friendly$/i, '')
      .replace(/\s*\((cupcake|cupcakes)\)\s*$/i, '')
      .trim();
  }

  /* ── SYNTHETIC FLAVORS — products not yet in the Shopify collection ──
     Must cover every flavor in the Daily Menu PDF so the box builder
     always offers the full menu, regardless of which flavors have been
     published as Shopify products yet. Keep this list and
     cupcake-schedule.js `syntheticFlavors` in lock-step. */
  var syntheticFlavors = [
    // Everyday (6)
    { id:  -1, title: 'Vanilla Vanilla',            handle: 'vanilla-vanilla',            image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id:  -2, title: 'Vanilla Chocolate',          handle: 'vanilla-chocolate',          image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id:  -3, title: 'Double Chocolate',           handle: 'double-chocolate',           image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id:  -4, title: 'Chocolate Vanilla',          handle: 'chocolate-vanilla',          image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id:  -5, title: 'Red Velvet',                 handle: 'red-velvet',                 image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id:  -6, title: 'Cookies & Cream',            handle: 'cookies-and-cream',          image: null, tags: [], isStaple: false, dayAvailability: [] },
    // Tuesday day-only (8)
    { id:  -7, title: 'Butterfinger',               handle: 'butterfinger',               image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id:  -8, title: 'Carrot',                     handle: 'carrot',                     image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id:  -9, title: 'Chocolate Chip Cookie Dough',handle: 'chocolate-chip-cookie-dough',image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -10, title: 'Raspberry Lemon',            handle: 'raspberry-lemon',            image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -11, title: 'Strawberry Cheesecake',      handle: 'strawberry-cheesecake',      image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -12, title: 'Strawberry Crunch',          handle: 'strawberry-crunch',          image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -13, title: 'Vanilla Caramel Latte',      handle: 'vanilla-latte-cupcake',      image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -14, title: 'Wet Coconut',                handle: 'wet-coconut',                image: null, tags: [], isStaple: false, dayAvailability: [] },
    // Wednesday day-only (8 unique)
    { id: -15, title: 'Cherry Cheesecake',          handle: 'cherry-cheesecake',          image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -16, title: 'Chocolate Ganache',          handle: 'chocolate-ganache',          image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -17, title: 'Chocolate Mint',             handle: 'chocolate-mint',             image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -18, title: 'Chocolate Strawberry',       handle: 'chocolate-strawberry',       image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -19, title: 'Lemon Cheesecake',           handle: 'lemon-cheesecake',           image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -20, title: 'Lemon Drop',                 handle: 'lemon-drop',                 image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -21, title: 'Pineapple Upside Down',      handle: 'pineapple-upside-down',      image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -22, title: 'Salted Caramel',             handle: 'salted-caramel',             image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -37, title: 'Caramel Banana Nut',         handle: 'caramel-banana-nut',         image: null, tags: [], isStaple: false, dayAvailability: [] },
    // Thursday unique (includes Vanilla Strawberry GF-exclusive tile)
    { id: -23, title: 'Chocolate Cheesecake',       handle: 'chocolate-cheesecake',       image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -24, title: 'Chocolate Salted Caramel',   handle: 'chocolate-salted-caramel',   image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -25, title: 'Hummingbird',                handle: 'hummingbird',                image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -26, title: 'Key Lime',                   handle: 'key-lime',                   image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -27, title: 'Peanut Butter Cookie',       handle: 'peanut-butter-cookie',       image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -28, title: 'Pina Colada',                handle: 'pina-colada',                image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -29, title: 'Vanilla Strawberry',         handle: 'vanilla-strawberry',         image: null, tags: [], isStaple: false, dayAvailability: [] },
    // Friday unique (includes Chocolate Swiss Roll GF-exclusive tile)
    { id: -30, title: 'German Chocolate',           handle: 'german-chocolate',           image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -31, title: 'Raspberry Merlot',           handle: 'raspberry-merlot',           image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -32, title: 'Swiss Roll',                 handle: 'chocolate-swiss-roll',       image: null, tags: [], isStaple: false, dayAvailability: [] },
    // Saturday unique
    { id: -33, title: 'Banana Bread Cheesecake',    handle: 'banana-bread-cheesecake',    image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -34, title: 'Dreamsicle',                 handle: 'dreamsicle',                 image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -35, title: 'Pancake Maple Bacon',        handle: 'pancake-maple-bacon',        image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -36, title: 'Peanut Butter Crunch',       handle: 'peanut-butter-crunch',       image: null, tags: [], isStaple: false, dayAvailability: [] },
    // Gluten-Friendly variants (9) — distinct products with their own SKUs
    // in Square (CUP-*GF). No Shopify products yet; these render as dedicated
    // tiles so every day shows 16 options (6 Everyday + 8 Daily + 2 GF).
    { id: -38, title: 'Carrot Gluten Friendly',               handle: 'carrot-gluten-friendly',               image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -39, title: 'Swiss Roll Gluten Friendly',           handle: 'chocolate-swiss-roll-gluten-friendly', image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -40, title: 'Chocolate Vanilla Gluten Friendly',    handle: 'chocolate-vanilla-gluten-friendly',    image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -41, title: 'Double Chocolate Gluten Friendly',     handle: 'double-chocolate-gluten-friendly',     image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -42, title: 'Hummingbird Gluten Friendly',          handle: 'hummingbird-gluten-friendly',          image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -43, title: 'Lemon Cheesecake Gluten Friendly',     handle: 'lemon-cheesecake-gluten-friendly',     image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -44, title: 'Lemon Drop Gluten Friendly',           handle: 'lemon-drop-gluten-friendly',           image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -45, title: 'Raspberry Lemon Gluten Friendly',      handle: 'raspberry-lemon-gluten-friendly',      image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -46, title: 'Red Velvet Gluten Friendly',           handle: 'red-velvet-gluten-friendly',           image: null, tags: [], isStaple: false, dayAvailability: [] },
    /* Summer 2026 — new flavors (Notion doc 2026-05-14). Products + SKUs
       already exist in Shopify; images not yet uploaded so wizard renders
       these with the no-image placeholder. Once a photo is added to the
       Shopify product the storefront-collection JSON entry takes over
       (de-dup at line 161 below). */
    { id: -47, title: 'Cotton Candy',                         handle: 'cotton-candy',                         image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -48, title: 'Nuddy Buddy',                          handle: 'nuddy-buddy',                          image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -49, title: "S'mores",                              handle: 's-mores',                              image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -50, title: 'Mint Cookies & Cream',                 handle: 'mint-cookies-and-cream',               image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -51, title: 'Berry Cheesecake',                     handle: 'berry-cheesecake',                     image: null, tags: [], isStaple: false, dayAvailability: [] },
    /* New GF synthetics introduced by Summer doc — no Shopify products
       yet, but the wizard needs entries to render them as tiles. */
    { id: -53, title: 'Vanilla Gluten Friendly',              handle: 'vanilla-gluten-friendly',              image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -54, title: 'Peanut Butter Cookie Gluten Friendly', handle: 'peanut-butter-cookie-gluten-friendly', image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -55, title: 'Chocolate Cheesecake Gluten Friendly', handle: 'chocolate-cheesecake-gluten-friendly', image: null, tags: [], isStaple: false, dayAvailability: [] },
    { id: -56, title: 'Blueberry Lemon Gluten Friendly',      handle: 'blueberry-lemon-gluten-friendly',      image: null, tags: [], isStaple: false, dayAvailability: [] }
  ];
  syntheticFlavors.forEach(function (sf) {
    var exists = allFlavors.some(function (f) { return _norm(f.title) === _norm(sf.title); });
    if (!exists) allFlavors.push(sf);
  });

  // Whitelist: only flavors that appear somewhere in the weekly schedule.
  // Normalized on both sides so e.g. "Pina Collada" matches "Piña Colada".
  var approvedFlavors = _schedule.allFlavorNamesLower();
  allFlavors = allFlavors.filter(function (f) {
    return approvedFlavors.indexOf(_norm(f.title)) !== -1;
  });

  // GF image fallback runs HERE — after real products + synthetic flavors are
  // merged and whitelisted — so every GF tile (including synthetic-only ones
  // like "Vanilla Gluten Friendly") borrows its base flavor's photo.
  fillGfImagesFromBase();

  // Normalized everyday list — same year-round across seasons.
  var everydayFlavors = _schedule.everyday.map(_norm);

  /* Date-aware lookups (added 2026-05-14 for seasonal flavor switching).
     We can NO LONGER snapshot `dailySchedule` / `gfPerDay` at init time
     because the data now depends on the customer's pickup date. These
     helpers return the normalized lists for the given (day, date) tuple
     by routing through `_schedule.flavorsForDay(day, date)`, which
     resolves the correct season internally. */
  function dailyScheduleFor(dayKey, date) {
    var info = _schedule.flavorsForDay(dayKey, date);
    return info.daily.map(_norm);
  }
  function gfPerDayFor(dayKey, date) {
    var info = _schedule.flavorsForDay(dayKey, date);
    return info.gf.map(_norm);
  }

  var validZips = config.deliveryZipCodes.split(',').map(function (z) { return z.trim(); });
  var currentSeason = config.currentSeason || 'winter';
  var deliveryFeeVariantId = config.deliveryFeeVariantId || null;
  var deliveryFeeAmount = config.deliveryFeeAmount || '5.00';

  /* ── COOKIE HELPERS (for checkout delivery method persistence) ── */
  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  /* ── URL PARAMS — pre-select box size via ?box=12 or ?box=custom ── */
  var urlParams = new URLSearchParams(window.location.search);
  var requestedBoxParam = urlParams.get('box') || '';
  var requestedBoxCustom = requestedBoxParam.toLowerCase() === 'custom';
  var requestedBoxSize = (!requestedBoxCustom && requestedBoxParam) ? parseInt(requestedBoxParam, 10) : 0;

  /* ── STATE ────────────────────────────────────────────────── */
  var state = {
    step: 1,
    orderType: '',       // 'Pickup' | 'Delivery'
    selectedDate: null,  // Date object
    dateStr: '',         // Formatted string
    dayName: '',         // 'Tuesday', etc.
    pickupTime: '',      // '11:00 AM - 12:00 PM' etc. — required before Continue.
                         // Added 2026-05-14: a customer placed an order without
                         // a pickup time, causing Shopify to auto-fill "Sunday
                         // at 5:23pm" (a closed day) on the admin pickup card.
    boxProduct: null,    // box object from allBoxes
    boxSize: 0,
    selections: [],      // [{flavorId, title, qty}]
    totalSelected: 0,
    requestedBoxSize: requestedBoxSize,    // From URL ?box=N
    requestedBoxCustom: requestedBoxCustom, // From URL ?box=custom
    isCustomBox: false,
    customVariantId: config.customCupcakeVariantId || null,
    customUnitPrice: config.customCupcakePrice || 0,
    customUnitPriceFormatted: config.customCupcakePriceFormatted || '$0.00',
    selectedPick: 'none'
  };

  /* ── DOM REFS ─────────────────────────────────────────────── */
  var $wizard = document.getElementById('order-wizard');
  if (!$wizard) return;

  var $progressFill = $wizard.querySelector('.ow-progress__fill');
  var $progressSteps = $wizard.querySelectorAll('.ow-progress__step');

  /* ── CALENDAR STATE ───────────────────────────────────────── */
  var calendarDate = new Date(); // currently viewed month
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  /* ═══════════════════════════════════════════════════════════
     STEP NAVIGATION
     ═══════════════════════════════════════════════════════════ */
  function goToStep(n) {
    if (n < 1 || n > 4) return;
    state.step = n;
    $wizard.setAttribute('data-step', n);

    // Hide all panels
    $wizard.querySelectorAll('[data-step-panel]').forEach(function (el) {
      el.classList.remove('is-visible');
      if (el.getAttribute('data-step-panel') === 'success') el.style.display = 'none';
    });

    // Show target panel
    var target = $wizard.querySelector('[data-step-panel="' + n + '"]');
    if (target) target.classList.add('is-visible');

    // Update progress
    var pct = (n / 4) * 100;
    $progressFill.style.width = pct + '%';
    $progressSteps.forEach(function (btn, i) {
      var stepNum = i + 1;
      btn.classList.toggle('is-active', stepNum === n);
      btn.classList.toggle('is-done', stepNum < n);
      btn.disabled = stepNum > n;
    });

    // Scroll to top of wizard
    $wizard.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Step-specific init
    if (n === 2) initCalendar();
    if (n === 3) initBoxGrid();
    if (n === 4) initBuilder();
  }

  function showSuccess() {
    $wizard.querySelectorAll('[data-step-panel]').forEach(function (el) {
      el.classList.remove('is-visible');
    });
    var success = $wizard.querySelector('[data-step-panel="success"]');
    success.style.display = '';
    success.classList.add('is-visible');
  }

  /* ═══════════════════════════════════════════════════════════
     STEP 1 — ORDER TYPE
     ═══════════════════════════════════════════════════════════ */
  function initStep1() {
    var cards = $wizard.querySelectorAll('.ow-type-card');
    var deliveryCheck = document.getElementById('ow-delivery-check');

    /* ── Fulfillment lock (2026-04-27) ───────────────────────────
       Shopify checkout supports ONE delivery method per order. If
       the customer already has items in the cart from a prior
       wizard run with a specific Order Type (Pickup OR Delivery),
       lock this wizard's selection to match — they can't mix
       Pickup + Delivery in one cart.

       To switch, customer must clear the cart first (link below
       the locked card). Reads `cart.attributes['Order Type']` set
       by addToCart in earlier passes.
       ──────────────────────────────────────────────────────────── */
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || cart.item_count === 0) return;
        var lockedType = cart.attributes && cart.attributes['Order Type'];
        if (lockedType !== 'Pickup' && lockedType !== 'Delivery') return;
        applyFulfillmentLock(lockedType);
      })
      .catch(function () { /* cart unreachable, no lock */ });

    function applyFulfillmentLock(lockedType) {
      cards.forEach(function (c) {
        var t = c.getAttribute('data-type');
        if (t === lockedType) {
          c.classList.add('is-selected');
        } else {
          c.classList.add('is-locked');
          c.setAttribute('aria-disabled', 'true');
          c.setAttribute('title', "You already have a " + lockedType + " order in progress. Clear your cart to switch.");
        }
      });

      // Show a banner explaining + offering to clear cart
      var headerEl = $wizard.querySelector('.ow-step--1 .ow-step__header');
      if (headerEl && !document.getElementById('ow-lock-banner')) {
        var banner = document.createElement('div');
        banner.id = 'ow-lock-banner';
        banner.className = 'ow-lock-banner';
        banner.setAttribute('role', 'status');
        banner.innerHTML =
          '<span class="ow-lock-banner__text">You\'re adding to your existing <strong>' + lockedType + '</strong> order. ' +
          'A single order can\'t mix pickup and delivery — to switch, clear your cart and start over.</span> ' +
          '<button type="button" class="ow-lock-banner__btn" id="ow-lock-clear">Clear cart &amp; switch</button>';
        headerEl.appendChild(banner);

        var clearBtn = document.getElementById('ow-lock-clear');
        if (clearBtn) clearBtn.addEventListener('click', function () {
          if (!confirm("Clear your cart and switch fulfillment method?")) return;
          fetch('/cart/clear.js', { method: 'POST' })
            .then(function () { window.location.reload(); })
            .catch(function () { window.location.reload(); });
        });
      }

      // Inject minimal styling once
      if (!document.getElementById('ow-lock-style')) {
        var style = document.createElement('style');
        style.id = 'ow-lock-style';
        style.textContent =
          '.ow-type-card.is-locked{opacity:.4;cursor:not-allowed;pointer-events:none;}' +
          '.ow-type-card.is-locked:hover{transform:none;border-color:var(--ow-border);}' +
          '.ow-lock-banner{margin-top:1rem;padding:.85rem 1rem;background:rgba(233,51,134,.08);border:1px solid rgba(233,51,134,.25);border-radius:8px;font-size:.92rem;line-height:1.4;display:flex;flex-wrap:wrap;align-items:center;gap:.75rem;justify-content:space-between;}' +
          '.ow-lock-banner__text{color:#333;flex:1 1 280px;}' +
          '.ow-lock-banner__btn{background:#E93386;color:#fff;border:0;padding:.45rem 1rem;border-radius:500px;font-size:.85rem;font-weight:500;cursor:pointer;font-family:inherit;}' +
          '.ow-lock-banner__btn:hover{background:#D31F71;}';
        document.head.appendChild(style);
      }
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        // Locked cards do nothing
        if (this.classList.contains('is-locked')) return;
        var type = this.getAttribute('data-type');

        // Toggle active state
        cards.forEach(function (c) { c.classList.remove('is-selected'); });
        this.classList.add('is-selected');

        state.orderType = type;

        // If the customer is bouncing back from step 2 to change order
        // type, re-filter the time picker so e.g. a pickup-only 5PM slot
        // disappears when they switch to delivery. (Mike + Renee + Chris
        // 2026-05-19 — expanded pickup hours.)
        var $timePicker = document.getElementById('ow-time-picker');
        if ($timePicker && $timePicker.style.display !== 'none' && typeof refreshTimeSlots === 'function') {
          refreshTimeSlots();
          var $label = document.getElementById('ow-time-picker__label');
          if ($label) $label.textContent = (type === 'Delivery' ? 'Delivery' : 'Pickup') + ' time';
        }

        if (type === 'Delivery') {
          deliveryCheck.style.display = '';
          document.getElementById('ow-zip-input').focus();
        } else {
          deliveryCheck.style.display = 'none';
          // Hide delivery details if previously shown
          var detailsPanel = document.getElementById('ow-delivery-details');
          if (detailsPanel) detailsPanel.style.display = 'none';
          // Pickup — go to step 2
          if (window.OrderScheduler) {
            window.OrderScheduler.setType('Pickup');
          }
          // Persist Order Type via cookie (survives into checkout domain)
          setCookie('cc_order_type', 'Pickup', 1);
          // Immediately persist Order Type to cart attributes
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { 'Order Type': 'Pickup' } })
          }).catch(function () {});
          document.dispatchEvent(new CustomEvent('order-scheduler:type-changed'));
          goToStep(2);
        }
      });
    });

    // Zip code check
    var $zipInput = document.getElementById('ow-zip-input');
    var $zipBtn = document.getElementById('ow-zip-check');
    var $zipResult = document.getElementById('ow-zip-result');

    function checkZip() {
      var zip = $zipInput.value.trim();
      if (zip.length !== 5) {
        $zipResult.textContent = 'Please enter a 5-digit zip code.';
        $zipResult.className = 'ow-delivery-check__result ow-delivery-check__result--error';
        return;
      }
      if (validZips.indexOf(zip) !== -1) {
        $zipResult.textContent = 'Great news — we deliver to your area!';
        $zipResult.className = 'ow-delivery-check__result ow-delivery-check__result--success';
        // Show delivery details panel (fee notice + instructions)
        var detailsPanel = document.getElementById('ow-delivery-details');
        if (detailsPanel) detailsPanel.style.display = '';
        // Store zip in session
        sessionStorage.setItem('cc_delivery_zip', zip);
        if (window.OrderScheduler) {
          window.OrderScheduler.setType('Delivery');
        }
        // Persist Order Type via cookie (survives into checkout domain)
        setCookie('cc_order_type', 'Delivery', 1);
        // Immediately persist Order Type to cart attributes
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { 'Order Type': 'Delivery' } })
        }).catch(function () {});
        document.dispatchEvent(new CustomEvent('order-scheduler:type-changed'));
      } else {
        $zipResult.textContent = 'Sorry, we don\'t deliver to that zip code yet. You can still pick up at our shop!';
        $zipResult.className = 'ow-delivery-check__result ow-delivery-check__result--error';
      }
    }

    $zipBtn.addEventListener('click', checkZip);
    $zipInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') checkZip();
    });

    // Zip re-validation on edit (2026-04-27): if the customer changes the zip
    // after a successful Check, the prior "approved" state must be invalidated
    // so they can't proceed to date selection with a stale zip. Hide the
    // delivery details panel (which contains Continue), clear the result
    // message, and wipe the session-stored zip until they re-Check.
    $zipInput.addEventListener('input', function () {
      var detailsPanel = document.getElementById('ow-delivery-details');
      if (detailsPanel) detailsPanel.style.display = 'none';
      if ($zipResult) {
        $zipResult.textContent = '';
        $zipResult.className = 'ow-delivery-check__result';
      }
      sessionStorage.removeItem('cc_delivery_zip');
    });

    // Delivery continue button — advance to date step
    var $deliveryContinue = document.getElementById('ow-delivery-continue');
    if ($deliveryContinue) {
      $deliveryContinue.addEventListener('click', function () {
        goToStep(2);
      });
    }

    // Persist delivery notes to sessionStorage on input
    var $deliveryNotes = document.getElementById('ow-delivery-notes');
    if ($deliveryNotes) {
      // Restore previous notes if any
      var saved = sessionStorage.getItem('cc_delivery_notes');
      if (saved) $deliveryNotes.value = saved;

      $deliveryNotes.addEventListener('input', function () {
        sessionStorage.setItem('cc_delivery_notes', this.value.trim());
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════
     STEP 2 — CUSTOM CALENDAR
     ═══════════════════════════════════════════════════════════ */
  function getMinDate() {
    if (window.OrderScheduler && window.OrderScheduler.getMinDate) {
      return window.OrderScheduler.getMinDate();
    }
    // Fallback: tomorrow
    var d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function getMaxDate() {
    if (window.OrderScheduler && window.OrderScheduler.getMaxDate) {
      return window.OrderScheduler.getMaxDate();
    }
    // Fallback: today + 21 days. Mirrors OrderScheduler.getMaxDate so the
    // box-builder calendar caps the same as the popup picker.
    var d = new Date();
    d.setDate(d.getDate() + 21);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  function isDateDisabled(date) {
    var day = date.getDay();
    // Sunday (0) or Monday (1) = closed
    if (day === 0 || day === 1) return true;
    // One-off bakery closures / holidays (e.g., July 4). Shares OrderScheduler's
    // single CLOSED_DATES list so the box-builder calendar stays in lock-step
    // with every other picker — no separate holiday list to maintain here.
    if (window.OrderScheduler && typeof window.OrderScheduler.isClosedDate === 'function'
        && window.OrderScheduler.isClosedDate(date)) return true;
    // Delivery-only blackout days (bakery open for pickup, but no deliveries).
    // Only disabled when the order is a Delivery — pickup keeps these days.
    // Shares OrderScheduler's DELIVERY_CLOSED_DATES list, same as every picker.
    if (state.orderType === 'Delivery'
        && window.OrderScheduler && typeof window.OrderScheduler.isDeliveryClosedDate === 'function'
        && window.OrderScheduler.isDeliveryClosedDate(date)) return true;
    var cmp = new Date(date);
    cmp.setHours(0, 0, 0, 0);
    // Before minimum allowed date (lead time + after-10pm bump, was 6pm pre-2026-05-14)
    if (cmp < getMinDate()) return true;
    // Past the 21-day max-advance cap
    if (cmp > getMaxDate()) return true;
    return false;
  }

  function renderCalendar() {
    var grid = document.getElementById('ow-cal-grid');
    var title = document.getElementById('ow-cal-title');
    if (!grid || !title) return;

    // Revalidate the current selection against the (possibly just-changed)
    // order type. If the customer picked a date as Pickup, went back and
    // switched to Delivery, and that date is a delivery-only blackout day, the
    // selection is no longer valid — clear it and reset the time picker /
    // preview / Continue so they must re-pick a deliverable day.
    if (state.selectedDate && isDateDisabled(state.selectedDate)) {
      state.selectedDate = null;
      state.dayName = '';
      state.dateStr = '';
      state.pickupTime = null;
      if (window.OrderScheduler) {
        if (typeof window.OrderScheduler.clearDate === 'function') window.OrderScheduler.clearDate();
        if (typeof window.OrderScheduler.clearTime === 'function') window.OrderScheduler.clearTime();
      }
      var $preview = document.getElementById('ow-date-preview');
      if ($preview) $preview.style.display = 'none';
      var $timePicker = document.getElementById('ow-time-picker');
      if ($timePicker) $timePicker.style.display = 'none';
      refreshDateNextEnabled();
    }

    var year = calendarDate.getFullYear();
    var month = calendarDate.getMonth();

    title.textContent = MONTH_NAMES[month] + ' ' + year;

    var firstDay = new Date(year, month, 1).getDay();
    var daysInMonth = new Date(year, month + 1, 0).getDate();
    var today = new Date();
    today.setHours(0, 0, 0, 0);

    var html = '';

    // Empty cells for days before the 1st
    for (var i = 0; i < firstDay; i++) {
      html += '<span class="ow-calendar__day ow-calendar__day--empty"></span>';
    }

    for (var d = 1; d <= daysInMonth; d++) {
      var date = new Date(year, month, d);
      var disabled = isDateDisabled(date);
      var isToday = date.getTime() === today.getTime();
      var isSelected = state.selectedDate && date.toDateString() === state.selectedDate.toDateString();

      var classes = 'ow-calendar__day';
      if (disabled) classes += ' ow-calendar__day--disabled';
      if (isToday) classes += ' ow-calendar__day--today';
      if (isSelected) classes += ' ow-calendar__day--selected';
      if (!disabled) classes += ' ow-calendar__day--available';

      if (disabled) {
        html += '<span class="' + classes + '">' + d + '</span>';
      } else {
        html += '<button type="button" class="' + classes + '" data-date="' + date.toISOString() + '">' + d + '</button>';
      }
    }

    grid.innerHTML = html;

    // Bind click events
    grid.querySelectorAll('button.ow-calendar__day--available').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dateObj = new Date(this.getAttribute('data-date'));
        selectDate(dateObj);
      });
    });
  }

  function selectDate(dateObj) {
    state.selectedDate = dateObj;
    state.dayName = DAY_NAMES[dateObj.getDay()];

    // Format: "Tuesday, February 24, 2026"
    state.dateStr = state.dayName + ', ' + MONTH_NAMES[dateObj.getMonth()] + ' ' + dateObj.getDate() + ', ' + dateObj.getFullYear();

    // Update OrderScheduler
    if (window.OrderScheduler) {
      window.OrderScheduler.setDate(state.dateStr, dateObj);
    }

    // Re-render calendar to show selection
    renderCalendar();

    // Show preview
    var availableFlavors = getAvailableFlavors(state.dayName, state.selectedDate);
    var $preview = document.getElementById('ow-date-preview');
    var $prevDay = document.getElementById('ow-date-preview-day');
    var $prevDate = document.getElementById('ow-date-preview-date');
    $prevDay.textContent = availableFlavors.length;
    $prevDate.textContent = state.dateStr;
    $preview.style.display = '';

    // Reveal the time picker. Continue stays DISABLED until a time is
    // chosen — gating on both date AND time prevents the order-without-
    // time bug Mike flagged 2026-05-14.
    var $timePicker = document.getElementById('ow-time-picker');
    if ($timePicker) {
      $timePicker.style.display = '';
      // Update label to match the order type (Pickup time / Delivery time)
      var $label = document.getElementById('ow-time-picker__label');
      if ($label) $label.textContent = (state.orderType === 'Delivery' ? 'Delivery' : 'Pickup') + ' time';
      // Apply day-of-week + order-type filtering to the slot list.
      // (Mike + Renee + Chris 2026-05-19: expanded pickup hours to 10AM-6PM
      // Tue-Fri / 10AM-4PM Sat so customers can pick up after work.)
      refreshTimeSlots();
      // If the customer already picked a time on a previous date selection,
      // keep that selection — same time slots apply to every pickup day.
      // Otherwise leave all slots unchecked and Continue disabled.

      // Auto-scroll the time picker into view so the user immediately
      // sees why Continue is still greyed out. Without this, the time
      // picker is well below the calendar fold on mobile and the user
      // had no signal that they still needed to make a second choice.
      // Mike 2026-05-14: "after the user selects the date, can you then
      // automatically scroll down to the time selector?"
      //
      // Tiny delay lets the calendar's re-render settle before scrolling
      // so the layout is stable. `block: 'center'` keeps a slice of the
      // calendar visible above on most screens, preserving context. We
      // skip the scroll entirely if the time picker already fits in the
      // viewport (large desktop) to avoid a jarring no-op animation.
      setTimeout(function () {
        var tpRect = $timePicker.getBoundingClientRect();
        var alreadyVisible = tpRect.top >= 0 && tpRect.bottom <= window.innerHeight;
        if (!alreadyVisible) {
          $timePicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 150);
    }
    refreshDateNextEnabled();
  }

  /**
   * Show only the time slots that are valid for the currently-selected
   * date + order type, hide the rest.
   *
   *   PICKUP, Tue-Fri  → 10AM–6PM (slots starting hour 10..17, all 8)
   *   PICKUP, Saturday → 10AM–4PM (slots starting hour 10..15, first 6)
   *   DELIVERY, any    → 11AM–3PM (slots starting hour 11..14, middle 4)
   *
   * If the currently-selected slot becomes hidden (e.g. customer was on a
   * Tuesday picking 5 PM, then switched to Saturday), the selection is
   * cleared and Continue greys back out — the customer has to actively
   * pick a slot that's still visible. Prevents shipping an order with a
   * time the bakery is closed.
   *
   * Mike + Renee + Chris 2026-05-19: expanded pickup hours so customers
   * can pick up after work.
   */
  function refreshTimeSlots() {
    if (!state.selectedDate) return;
    var isDelivery = state.orderType === 'Delivery';
    var isSaturday = state.dayName === 'Saturday';

    var minHour, maxStartHour;
    if (isDelivery) {
      minHour = 11; maxStartHour = 14;  // 11AM-3PM window
    } else if (isSaturday) {
      minHour = 10; maxStartHour = 15;  // 10AM-4PM window (last slot 3-4)
    } else {
      minHour = 10; maxStartHour = 17;  // 10AM-6PM window (last slot 5-6)
    }

    var anyVisible = false;
    var selectedStillVisible = false;
    var slots = document.querySelectorAll('#ow-time-picker .ow-time-slot');
    slots.forEach(function (btn) {
      var startHour = parseInt(btn.getAttribute('data-start-hour'), 10);
      var visible = !isNaN(startHour) && startHour >= minHour && startHour <= maxStartHour;
      btn.style.display = visible ? '' : 'none';
      if (visible) anyVisible = true;
      if (visible && state.pickupTime === btn.getAttribute('data-time')) {
        selectedStillVisible = true;
      }
    });

    // If the previously-picked slot got filtered out, clear it so the
    // customer is forced to pick a fresh, valid slot.
    if (state.pickupTime && !selectedStillVisible) {
      state.pickupTime = '';
      slots.forEach(function (btn) {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-checked', 'false');
      });
      if (window.OrderScheduler && typeof window.OrderScheduler.setTime === 'function') {
        window.OrderScheduler.setTime('');
      }
      refreshDateNextEnabled();
    }

    return anyVisible;
  }

  function selectTime(timeStr) {
    state.pickupTime = timeStr;
    // Update slot UI
    var slots = document.querySelectorAll('#ow-time-picker .ow-time-slot');
    slots.forEach(function (btn) {
      var match = btn.getAttribute('data-time') === timeStr;
      btn.classList.toggle('is-selected', match);
      btn.setAttribute('aria-checked', match ? 'true' : 'false');
    });
    // Mirror to OrderScheduler if it exists (legacy date-filter sidebar
    // reads from the same global state).
    if (window.OrderScheduler && typeof window.OrderScheduler.setTime === 'function') {
      window.OrderScheduler.setTime(timeStr);
    }
    refreshDateNextEnabled();
  }

  function refreshDateNextEnabled() {
    var btn = document.getElementById('ow-date-next');
    if (!btn) return;
    btn.disabled = !(state.selectedDate && state.pickupTime);
  }

  function initCalendar() {
    // Start calendar at current month or the month of min date
    var min = getMinDate();
    var now = new Date();
    if (min.getMonth() > now.getMonth() || min.getFullYear() > now.getFullYear()) {
      calendarDate = new Date(min.getFullYear(), min.getMonth(), 1);
    } else {
      calendarDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    renderCalendar();
    updateCalendarNav();

    // Bind time slot click handlers (mandatory time selection — 2026-05-14)
    document.querySelectorAll('#ow-time-picker .ow-time-slot').forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectTime(this.getAttribute('data-time'));
      });
    });

    document.getElementById('ow-cal-prev').onclick = function () {
      calendarDate.setMonth(calendarDate.getMonth() - 1);
      renderCalendar();
      updateCalendarNav();
    };
    document.getElementById('ow-cal-next').onclick = function () {
      calendarDate.setMonth(calendarDate.getMonth() + 1);
      renderCalendar();
      updateCalendarNav();
    };
  }

  /** Disable prev button if already at or before current month */
  function updateCalendarNav() {
    var now = new Date();
    var prevBtn = document.getElementById('ow-cal-prev');
    // Disable Prev when we're already at or before the current month.
    // The Next arrow stays free at all times — even past the 21-day window —
    // so customers can browse the calendar to see what's ahead. Dates in
    // out-of-range months still render disabled by `isDateDisabled`.
    var isPastOrCurrent = calendarDate.getFullYear() < now.getFullYear() ||
      (calendarDate.getFullYear() === now.getFullYear() && calendarDate.getMonth() <= now.getMonth());
    prevBtn.disabled = isPastOrCurrent;
    prevBtn.style.opacity = isPastOrCurrent ? '0.3' : '1';
    prevBtn.style.pointerEvents = isPastOrCurrent ? 'none' : '';
  }

  /* ═══════════════════════════════════════════════════════════
     DELIVERY FEE MANAGEMENT
     ═══════════════════════════════════════════════════════════ */

  /**
   * Add the hidden Delivery Fee product to the cart.
   * Silently fails if the product is already in the cart or variant is missing.
   */
  function addDeliveryFee() {
    // Delivery fee removed — no-op
    return Promise.resolve();
  }

  /**
   * Remove the Delivery Fee product from the cart if present.
   */
  function removeDeliveryFee() {
    if (!deliveryFeeVariantId) return Promise.resolve();

    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var feeItem = cart.items.find(function (item) {
          return String(item.variant_id) === String(deliveryFeeVariantId);
        });
        if (!feeItem) return; // Not in cart

        return fetch(config.cartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: feeItem.key,
            quantity: 0
          })
        });
      })
      .catch(function (err) {
        console.warn('[OrderWizard] Could not remove delivery fee:', err);
      });
  }

  /**
   * Get delivery instructions from the textarea, if any.
   */
  function getDeliveryNotes() {
    var textarea = document.getElementById('ow-delivery-notes');
    return textarea ? textarea.value.trim() : '';
  }

  /* ═══════════════════════════════════════════════════════════
     FLAVOR FILTERING LOGIC
     ═══════════════════════════════════════════════════════════ */

  /**
   * Override isStaple based on the daily menu JPEG — 6 everyday flavors.
   */
  allFlavors.forEach(function (f) {
    f.isStaple = everydayFlavors.indexOf(_norm(f.title)) !== -1;
  });

  /**
   * Determine which flavors are available on a given day.
   * Uses the dailySchedule from the daily menu.
   * Each day = 4 everyday + 8 daily + 2 GF = 14 total.
   * Order: Everyday (non-GF) → Daily (non-GF) → GF (2 at end).
   * GF flavors are included in the day's lineup even if they aren't
   * listed in the base everyday/daily arrays — they're their own slot.
   * Falls back to the tag system if the day isn't in the schedule.
   */
  function getAvailableFlavors(dayName, date) {
    var dayLower = dayName.toLowerCase();
    /* Resolve schedule for the customer's chosen pickup date.
       Seasonal switching: dates ≥ 2026-06-02 use the summer menu;
       earlier dates use the spring menu (the current set). */
    var schedule = dailyScheduleFor(dayLower, date);
    var gfNames = gfPerDayFor(dayLower, date);

    if (schedule && schedule.length) {
      /* Get GF set for this day (already normalized) */
      var gfSet = {};
      gfNames.forEach(function (n) { gfSet[n] = true; });

      /* Build a combined set of everyday + daily-specific + GF flavors.
         GF items must be included here so they surface as selectable
         options alongside the base daily flavors. */
      var daySet = everydayFlavors.concat(schedule).concat(gfNames);
      var matched = allFlavors.filter(function (f) {
        return daySet.indexOf(_norm(f.title)) !== -1;
      });

      /* Tag each flavor with _isGfToday for sorting */
      matched.forEach(function (f) {
        f._isGfToday = !!gfSet[_norm(f.title)];
      });

      /* Sort: Everyday (with or without GF badge) → Daily non-GF → Daily GF.
         Staple (everyday) items always come first. GF-only items (items
         that are GF today but NOT in the everyday pool) sort last. */
      matched.sort(function (a, b) {
        /* Staples first — even if they're also GF today */
        if (a.isStaple && !b.isStaple) return -1;
        if (!a.isStaple && b.isStaple) return 1;
        /* Among non-staple (daily) items: non-GF before GF */
        if (!a.isStaple && !b.isStaple) {
          if (a._isGfToday && !b._isGfToday) return 1;
          if (!a._isGfToday && b._isGfToday) return -1;
        }
        return 0;
      });

      return matched;
    }

    /* Fallback to tag-based filtering for days not in schedule */
    var everyTag = 'every-' + dayLower;
    var seasonTag = currentSeason + '-' + dayLower;

    return allFlavors.filter(function (f) {
      if (f.isStaple) return true;
      var tags = f.tags || [];
      if (tags.indexOf(everyTag) !== -1) return true;
      if (tags.indexOf(seasonTag) !== -1) return true;
      return false;
    });
  }

  /* ═══════════════════════════════════════════════════════════
     STEP 3 — BOX SIZE
     ═══════════════════════════════════════════════════════════ */
  function initBoxGrid() {
    var grid = document.getElementById('ow-box-grid');
    if (!grid) return;

    // Sort boxes: largest first (12, 6, 4, 2)
    var sorted = allBoxes.slice().sort(function (a, b) { return b.maxQty - a.maxQty; });

    // Only re-render if we have JS data and no server-rendered cards
    // (In most cases, Liquid already rendered the cards)

    var cards = grid.querySelectorAll('.ow-box-card');
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        selectBox(this, cards);
      });
    });

    // Auto-select box from URL param ?box=custom or ?box=N
    if (state.requestedBoxCustom) {
      var customCard = grid.querySelector('.ow-box-card--custom');
      if (customCard) {
        setTimeout(function () {
          selectBox(customCard, cards);
        }, 300);
        state.requestedBoxCustom = false;
      }
    } else if (state.requestedBoxSize > 0) {
      var matchCard = null;
      cards.forEach(function (c) {
        if (parseInt(c.getAttribute('data-box-size')) === state.requestedBoxSize) {
          matchCard = c;
        }
      });
      if (matchCard) {
        // Small delay so the UI renders step 3 briefly before advancing
        setTimeout(function () {
          selectBox(matchCard, cards);
        }, 300);
        // Clear so we don't auto-select again if user navigates back
        state.requestedBoxSize = 0;
      }
    }
  }

  /** Select a box card — shared by click handler and URL auto-select */
  function selectBox(card, allCards) {
    var boxIdAttr = card.getAttribute('data-box-id');
    var boxSize = parseInt(card.getAttribute('data-box-size'));

    allCards.forEach(function (c) { c.classList.remove('is-selected'); });
    card.classList.add('is-selected');

    var stepper = document.getElementById('ow-custom-stepper');

    // Custom box card — "Build Your Own Box" (unlimited, no stepper)
    if (boxIdAttr === 'custom') {
      state.isCustomBox = true;
      state.boxProduct = {
        id: 'custom',
        variantId: state.customVariantId,
        title: 'Build Your Own Box',
        priceFormatted: state.customUnitPriceFormatted + ' each',
        price: 0
      };
      state.boxSize = 0; // 0 = unlimited
      state.selections = [];
      state.totalSelected = 0;
      applyPickEligibility();
      goToStep(4);
      return;
    }

    // Standard box card
    state.isCustomBox = false;

    var boxId = parseInt(boxIdAttr);
    state.boxProduct = allBoxes.find(function (b) { return b.id === boxId; });
    state.boxSize = boxSize;
    state.selections = [];
    state.totalSelected = 0;

    applyPickEligibility();
    goToStep(4);
  }

  /* ── Update cupcake pick prices based on box size (2026-04-27) ──
     Picks are sold as size-bundled products (4-pack $3.50, 6-pack $5,
     12-pack $10) — see snippets/product-modifier-groups.liquid and the
     Shopify pick-bundle products synced from Square via DPL. The UI
     here shows the per-box-size bundle price the customer will be
     charged. Custom box mode and 1/2-count boxes don't get picks
     (see applyPickEligibility), so this function only renders meaningful
     prices for boxSize 4, 6, and 12. */
  var pickBundlePrices = { 4: 3.50, 6: 5.00, 12: 10.00 };
  function updatePickPrices() {
    var bundlePrice = pickBundlePrices[state.boxSize];
    $wizard.querySelectorAll('.ow-picks__option').forEach(function (btn) {
      var pickType = btn.getAttribute('data-pick');
      var priceSpan = btn.querySelector('.ow-picks__price');
      if (!priceSpan) return;
      if (pickType === 'none') {
        priceSpan.textContent = 'Free';
      } else if (bundlePrice) {
        priceSpan.textContent = '+ $' + bundlePrice.toFixed(2);
      } else {
        priceSpan.textContent = '';
      }
    });
  }

  /* ── Toggle the Cupcake Pick add-on section by box size (2026-04-27) ──
     Business rule: picks are sold as size-specific bundles (4/6/12 only).
     They are offered only on the standard 4-, 6-, and 12-count boxes.
     Hidden on:
       • 1- and 2-count boxes (no matching pick bundle exists)
       • Custom amount boxes (decomposes into multiple boxes — picks would
         have to split unpredictably; cleaner to require a standard size)
     When hidden, the prior selection is reset to "none" so it doesn't
     sneak into the cart. */
  function applyPickEligibility() {
    var picksSection = document.getElementById('ow-picks-section');
    if (!picksSection) return;
    var showPicks = !state.isCustomBox && (state.boxSize === 4 || state.boxSize === 6 || state.boxSize === 12);
    picksSection.style.display = showPicks ? '' : 'none';
    if (!showPicks) {
      state.selectedPick = 'none';
      $wizard.querySelectorAll('.ow-picks__option').forEach(function (b) {
        b.classList.remove('is-active');
      });
      var noPick = $wizard.querySelector('.ow-picks__option[data-pick="none"]');
      if (noPick) noPick.classList.add('is-active');
    }
  }

  /* ═══════════════════════════════════════════════════════════
     STEP 4 — BUILD YOUR BOX
     ═══════════════════════════════════════════════════════════ */
  function initBuilder() {
    if (!state.dayName || (!state.boxSize && !state.isCustomBox)) return;

    var flavors = getAvailableFlavors(state.dayName, state.selectedDate);
    var maxQty = state.isCustomBox ? 0 : state.boxSize;

    // Update seasonal filter button with day-of-week name
    var seasonalBtn = $wizard.querySelector('[data-filter="seasonal"]');
    if (seasonalBtn && state.dayName) {
      seasonalBtn.textContent = state.dayName + "'s Flavors";
    }

    // Update subtitle
    var $subtitle = document.getElementById('ow-builder-subtitle');
    if (state.isCustomBox) {
      if (state.totalSelected > 0) {
        var runTotal = (state.totalSelected * state.customUnitPrice / 100).toFixed(2);
        $subtitle.innerHTML = '<strong>' + state.totalSelected + ' cupcake' + (state.totalSelected !== 1 ? 's' : '') + '</strong> &mdash; $' + runTotal + ' total (' + state.customUnitPriceFormatted + ' each)';
      } else {
        $subtitle.innerHTML = 'Add as many as you like &mdash; ' + state.customUnitPriceFormatted + ' each';
      }
    } else {
      document.getElementById('ow-builder-remaining').textContent = state.totalSelected;
      document.getElementById('ow-builder-total').textContent = maxQty;
    }

    // Update order summary
    document.getElementById('ow-summary-type').textContent = state.orderType;
    document.getElementById('ow-summary-date').textContent = state.dateStr;
    var $summaryTime = document.getElementById('ow-summary-time');
    if ($summaryTime) $summaryTime.textContent = state.pickupTime || '—';
    document.getElementById('ow-summary-box').textContent = state.boxProduct ? state.boxProduct.title : maxQty + ' Count';

    // Show total row for custom box (running total) or standard box (fixed price)
    var $totalRow = document.getElementById('ow-summary-total-row');
    var $totalVal = document.getElementById('ow-summary-total');
    if (state.isCustomBox) {
      $totalRow.style.display = '';
      var initTotal = (state.totalSelected * state.customUnitPrice / 100).toFixed(2);
      $totalVal.textContent = state.totalSelected > 0 ? ('$' + initTotal) : '$0.00';
    } else if (state.boxProduct && state.boxProduct.priceFormatted) {
      $totalRow.style.display = '';
      $totalVal.textContent = state.boxProduct.priceFormatted;
    } else {
      $totalRow.style.display = 'none';
    }

    // Render flavor grid
    renderFlavorGrid(flavors, 'all');

    // Filter / Surprise / Clear buttons. `initBuilder()` runs every time the
    // user enters Step 4 (e.g. via "Build Another Box"), so we guard each bind
    // with `dataset.bound` to prevent stacking listeners. Inside the filter
    // handler we re-fetch flavors via `getAvailableFlavors(state.dayName, state.selectedDate)` so
    // day-changes between Step 4 entries are reflected (the original closure
    // would have captured a stale flavors array).
    $wizard.querySelectorAll('.ow-builder__filter').forEach(function (btn) {
      if (btn.dataset.bound === 'true') return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        $wizard.querySelectorAll('.ow-builder__filter').forEach(function (b) { b.classList.remove('is-active'); });
        this.classList.add('is-active');
        var filter = this.getAttribute('data-filter');
        var freshFlavors = getAvailableFlavors(state.dayName, state.selectedDate);
        renderFlavorGrid(freshFlavors, filter);
      });
    });

    // Surprise Me button — bind once; the disabled state must update every initBuilder call.
    var surpriseBtn = document.getElementById('ow-surprise-me');
    if (surpriseBtn) {
      if (surpriseBtn.dataset.bound !== 'true') {
        surpriseBtn.dataset.bound = 'true';
        surpriseBtn.addEventListener('click', surpriseMe);
      }
      surpriseBtn.disabled = state.isCustomBox ? false : (state.totalSelected >= state.boxSize);
    }

    // Clear Selections button — bind once.
    var clearBtn = document.getElementById('ow-clear-selections');
    if (clearBtn && clearBtn.dataset.bound !== 'true') {
      clearBtn.dataset.bound = 'true';
      clearBtn.addEventListener('click', function () {
        state.selections = [];
        state.totalSelected = 0;
        refreshBuilder();
        clearBtn.style.display = 'none';
      });
    }

    // Build box visual
    renderBoxVisual();
    renderSelectionsList();
    updateAddToCartBtn();
    updatePickPrices();
  }

  function renderFlavorGrid(flavors, filter) {
    var grid = document.getElementById('ow-flavor-grid');
    if (!grid) return;

    // Flavors are already sorted by getAvailableFlavors: Everyday → Daily → GF
    var sorted = flavors.slice();

    var filtered = sorted;
    if (filter === 'staple') {
      filtered = sorted.filter(function (f) { return f.isStaple; });
    } else if (filter === 'seasonal') {
      filtered = sorted.filter(function (f) { return !f.isStaple; });
    }

    if (filtered.length === 0) {
      grid.innerHTML = '<p class="ow-builder__no-results">No flavors match this filter.</p>';
      return;
    }

    var html = '';
    filtered.forEach(function (f) {
      var existing = state.selections.find(function (s) { return s.flavorId === f.id; });
      var qty = existing ? existing.qty : 0;
      var isFull = state.isCustomBox ? false : (state.totalSelected >= state.boxSize);

      html += '<div class="ow-flavor-card' + (qty > 0 ? ' is-added' : '') + '" data-flavor-id="' + f.id + '">';

      var tileLabel = displayTitle(f.title);
      if (f.image) {
        html += '<div class="ow-flavor-card__img-wrap">';
        html += '<img src="' + f.image + '" alt="' + tileLabel + '" loading="lazy" width="120" height="120">';
        html += '</div>';
      } else {
        html += '<div class="ow-flavor-card__img-wrap ow-flavor-card__img-wrap--placeholder">';
        html += '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/></svg>';
        html += '</div>';
      }

      html += '<span class="ow-flavor-card__name">' + tileLabel + '</span>';

      // Flavor tag badges — day-specific flavors show e.g. "Tuesday Flavor"; GF always pairs with the day badge.
      var dayLabel = state.dayName ? state.dayName + ' Flavor' : 'Daily Flavor';
      var badges = '';
      if (f._isGfToday) {
        badges += '<span class="ow-flavor-badge ow-flavor-badge--daily">' + dayLabel + '</span>';
        badges += '<span class="ow-flavor-badge ow-flavor-badge--gf">Gluten-Friendly</span>';
      } else if (f.isStaple) {
        badges += '<span class="ow-flavor-badge ow-flavor-badge--everyday">Everyday</span>';
      } else {
        badges += '<span class="ow-flavor-badge ow-flavor-badge--daily">' + dayLabel + '</span>';
      }
      html += '<div class="ow-flavor-card__badges">' + badges + '</div>';

      // Quantity controls
      html += '<div class="ow-flavor-card__controls">';
      if (qty > 0) {
        html += '<button class="ow-flavor-card__btn ow-flavor-card__btn--minus" data-action="remove" data-flavor-id="' + f.id + '">−</button>';
        html += '<span class="ow-flavor-card__qty">' + qty + '</span>';
        html += '<button class="ow-flavor-card__btn ow-flavor-card__btn--plus" data-action="add" data-flavor-id="' + f.id + '"' + (isFull ? ' disabled' : '') + '>+</button>';
      } else {
        html += '<button class="ow-flavor-card__add-btn" data-action="add" data-flavor-id="' + f.id + '"' + (isFull ? ' disabled' : '') + '>Add to Box</button>';
      }
      html += '</div>';

      html += '</div>';
    });

    grid.innerHTML = html;

    // Bind events
    grid.querySelectorAll('[data-action]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (this.disabled) return;
        var action = this.getAttribute('data-action');
        var flavorId = parseInt(this.getAttribute('data-flavor-id'));
        if (action === 'add') {
          // Double-check cap before adding
          if (!state.isCustomBox && state.totalSelected >= state.boxSize) return;
          addFlavor(flavorId);
        } else if (action === 'remove') {
          removeFlavor(flavorId);
        }
      });
    });
  }

  function addFlavor(flavorId) {
    if (!state.isCustomBox && state.totalSelected >= state.boxSize) return;

    var existing = state.selections.find(function (s) { return s.flavorId === flavorId; });
    if (existing) {
      existing.qty++;
    } else {
      var flavor = allFlavors.find(function (f) { return f.id === flavorId; });
      if (!flavor) return;
      state.selections.push({
        flavorId: flavorId,
        title: flavor.title,
        image: flavor.image,
        qty: 1
      });
    }

    state.totalSelected++;
    refreshBuilder();
  }

  function removeFlavor(flavorId) {
    var existing = state.selections.find(function (s) { return s.flavorId === flavorId; });
    if (!existing) return;

    existing.qty--;
    state.totalSelected--;

    if (existing.qty <= 0) {
      state.selections = state.selections.filter(function (s) { return s.flavorId !== flavorId; });
    }

    refreshBuilder();
  }

  function surpriseMe() {
    // For custom mode, top up to 12 cupcakes total. Was previously
    // Math.max(6, 12 - totalSelected) which kept adding 6 each click even past
    // the 12-cup cap (a customer at 12 would jump to 18 on every Surprise Me).
    var remaining = state.isCustomBox
      ? Math.max(0, 12 - state.totalSelected)
      : (state.boxSize - state.totalSelected);
    if (remaining <= 0) return;

    var flavors = getAvailableFlavors(state.dayName, state.selectedDate);
    if (flavors.length === 0) return;

    // Fill remaining slots with random picks from available flavors
    for (var i = 0; i < remaining; i++) {
      var randomIndex = Math.floor(Math.random() * flavors.length);
      var flavor = flavors[randomIndex];

      // Inline add logic (avoid calling addFlavor which triggers refreshBuilder each time)
      var existing = state.selections.find(function (s) { return s.flavorId === flavor.id; });
      if (existing) {
        existing.qty++;
      } else {
        state.selections.push({
          flavorId: flavor.id,
          title: flavor.title,
          image: flavor.image,
          qty: 1
        });
      }
      state.totalSelected++;
    }

    // Single refresh after all additions
    refreshBuilder();
  }

  function refreshBuilder() {
    // Update counter / running total
    var $subtitle = document.getElementById('ow-builder-subtitle');
    if (state.isCustomBox) {
      if (state.totalSelected > 0) {
        var runTotal = (state.totalSelected * state.customUnitPrice / 100).toFixed(2);
        $subtitle.innerHTML = '<strong>' + state.totalSelected + ' cupcake' + (state.totalSelected !== 1 ? 's' : '') + '</strong> &mdash; $' + runTotal + ' total (' + state.customUnitPriceFormatted + ' each)';
      } else {
        $subtitle.innerHTML = 'Add as many as you like &mdash; ' + state.customUnitPriceFormatted + ' each';
      }
    } else {
      document.getElementById('ow-builder-remaining').textContent = state.totalSelected;
    }

    // Update summary total row
    var $totalRow = document.getElementById('ow-summary-total-row');
    var $totalVal = document.getElementById('ow-summary-total');
    if (state.isCustomBox && $totalRow && $totalVal) {
      $totalRow.style.display = '';
      var summaryTotal = (state.totalSelected * state.customUnitPrice / 100).toFixed(2);
      $totalVal.textContent = state.totalSelected > 0 ? ('$' + summaryTotal) : '$0.00';
    }

    // Re-render flavors (preserving current filter)
    var activeFilter = $wizard.querySelector('.ow-builder__filter.is-active');
    var filter = activeFilter ? activeFilter.getAttribute('data-filter') : 'all';
    var flavors = getAvailableFlavors(state.dayName, state.selectedDate);
    renderFlavorGrid(flavors, filter);

    // Update box visual
    renderBoxVisual();

    // Update selections list
    renderSelectionsList();

    // Update add to cart button
    updateAddToCartBtn();

    // Update Surprise Me button
    var surpriseBtn = document.getElementById('ow-surprise-me');
    if (surpriseBtn) {
      surpriseBtn.disabled = state.isCustomBox ? false : (state.totalSelected >= state.boxSize);
    }

    // Update Clear Selections button
    var clearBtn = document.getElementById('ow-clear-selections');
    if (clearBtn) clearBtn.style.display = state.totalSelected > 0 ? '' : 'none';

    // Update pick prices for custom box mode (qty changes as cupcakes are added)
    if (state.isCustomBox) updatePickPrices();
  }

  function renderBoxVisual() {
    var visual = document.getElementById('ow-box-visual');
    if (!visual) return;

    // Build flat list of slots
    var slots = [];
    state.selections.forEach(function (s) {
      for (var i = 0; i < s.qty; i++) {
        slots.push(s);
      }
    });

    var html = '';

    if (state.isCustomBox) {
      // Unlimited mode: show only filled slots (no empty placeholders)
      var totalSlots = slots.length;
      var cols = totalSlots <= 2 ? 2 : totalSlots <= 4 ? 2 : totalSlots <= 6 ? 3 : totalSlots <= 9 ? 3 : 4;
      visual.style.setProperty('--box-cols', cols);

      for (var i = 0; i < totalSlots; i++) {
        var s = slots[i];
        html += '<div class="ow-box-slot ow-box-slot--filled">';
        if (s.image) {
          html += '<img src="' + s.image + '" alt="' + displayTitle(s.title) + '" width="60" height="60">';
        }
        html += '<span class="ow-box-slot__label">' + displayTitle(s.title) + '</span>';
        html += '</div>';
      }

      // Show one empty "+" slot to invite more additions
      html += '<div class="ow-box-slot ow-box-slot--empty">';
      html += '<span class="ow-box-slot__number">+</span>';
      html += '</div>';
    } else {
      // Standard mode: show exactly boxSize slots
      var maxQty = state.boxSize;
      var cols = maxQty <= 2 ? 2 : maxQty <= 4 ? 2 : maxQty <= 6 ? 3 : 4;
      visual.style.setProperty('--box-cols', cols);

      for (var i = 0; i < maxQty; i++) {
        if (i < slots.length) {
          var s = slots[i];
          html += '<div class="ow-box-slot ow-box-slot--filled">';
          if (s.image) {
            html += '<img src="' + s.image + '" alt="' + displayTitle(s.title) + '" width="60" height="60">';
          }
          html += '<span class="ow-box-slot__label">' + displayTitle(s.title) + '</span>';
          html += '</div>';
        } else {
          html += '<div class="ow-box-slot ow-box-slot--empty">';
          html += '<span class="ow-box-slot__number">' + (i + 1) + '</span>';
          html += '</div>';
        }
      }
    }

    visual.innerHTML = html;
  }

  function renderSelectionsList() {
    var list = document.getElementById('ow-selections-list');
    if (!list) return;

    if (state.selections.length === 0) {
      var isMobile = window.innerWidth <= 768;
      var emptyMsg = isMobile ? 'Add flavors below to fill your box' : 'Add flavors from the left to fill your box';
      list.innerHTML = '<p class="ow-builder__empty-msg">' + emptyMsg + '</p>';
      return;
    }

    var html = '';
    state.selections.forEach(function (s) {
      html += '<div class="ow-selection-row">';
      html += '<span class="ow-selection-row__name">' + displayTitle(s.title) + '</span>';
      html += '<span class="ow-selection-row__qty">&times;' + s.qty + '</span>';
      html += '<button class="ow-selection-row__remove" data-action="remove" data-flavor-id="' + s.flavorId + '" aria-label="Remove">&times;</button>';
      html += '</div>';
    });
    list.innerHTML = html;

    // Bind remove buttons
    list.querySelectorAll('[data-action="remove"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var flavorId = parseInt(this.getAttribute('data-flavor-id'));
        // Remove all of this flavor
        var sel = state.selections.find(function (s) { return s.flavorId === flavorId; });
        if (sel) {
          state.totalSelected -= sel.qty;
          state.selections = state.selections.filter(function (s) { return s.flavorId !== flavorId; });
          refreshBuilder();
        }
      });
    });
  }

  function updateAddToCartBtn() {
    var btn = document.getElementById('ow-add-to-cart');
    if (!btn) return;

    // Pick bundle price — source of truth is `pickBundlePrices` (line 838),
    // which must mirror the actual Shopify pick-bundle product variant prices
    // (4-pack = $3.50, 6-pack = $5.00, 12-pack = $10.00). Previous code calculated
    // this as $0.50 per cupcake (pickQty * 50 cents), which produced $2/$3/$6 for
    // 4/6/12 boxes — different from both the button label and the Shopify charge,
    // so the customer saw a phantom upcharge at checkout. Now the button-total
    // math agrees with `updatePickPrices()` and with the cart line item that's
    // actually added (assets/order-wizard.js:1507). Picks are only offered on
    // standard 4/6/12 boxes (custom boxes are excluded by applyPickEligibility,
    // which resets selectedPick to 'none'), so the lookup is always defined here.
    var pickAddon = 0;
    if (state.selectedPick && state.selectedPick !== 'none') {
      var bundleDollars = pickBundlePrices[state.boxSize] || 0;
      pickAddon = Math.round(bundleDollars * 100); // cents
    }

    if (state.isCustomBox) {
      // Unlimited mode: enable as soon as 1+ cupcakes added
      var hasItems = state.totalSelected >= 1;
      btn.disabled = !hasItems;
      if (hasItems) {
        var total = ((state.totalSelected * state.customUnitPrice + pickAddon) / 100).toFixed(2);
        btn.textContent = 'Add to Cart — $' + total + ' (' + state.totalSelected + ' cupcake' + (state.totalSelected !== 1 ? 's' : '') + ')';
      } else {
        btn.textContent = 'Add cupcakes to your box';
      }
    } else {
      // Standard mode: enable when box is full
      var isFull = state.totalSelected === state.boxSize;
      btn.disabled = !isFull;
      if (isFull) {
        var boxPrice = state.boxProduct ? state.boxProduct.price : 0; // cents
        var grandTotal = ((boxPrice + pickAddon) / 100).toFixed(2);
        btn.textContent = 'Add to Cart — $' + grandTotal;
      } else {
        btn.textContent = state.totalSelected + ' of ' + state.boxSize + ' — Fill your box to continue';
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════
     ADD TO CART
     ═══════════════════════════════════════════════════════════ */
  function handleAddToCart() {
    if (state.isCustomBox) {
      if (state.totalSelected < 1 || !state.customVariantId) return;
    } else {
      if (state.totalSelected !== state.boxSize || !state.boxProduct) return;
    }

    var btn = document.getElementById('ow-add-to-cart');
    btn.disabled = true;
    btn.textContent = 'Adding...';

    // Build flavor summary string — this is the single source of truth for
    // both customer-facing display AND the internal /pages/bake-list parser.
    // The parser regex-extracts "Nx FlavorName" pairs from this string.
    var flavorSummary = state.selections.map(function (s) {
      return s.qty + 'x ' + s.title;
    }).join(', ');

    // Line item properties.
    // Note (2026-05-11): `Order Type` and `Pickup/Delivery Date` used to live
    // here too, but they're order-level data that's already mirrored to the
    // cart attributes (see `attrs` block below). Carrying them per-line-item
    // produced two bugs: (1) when the customer toggled Ship ↔ Pickup at
    // Shopify's native checkout the per-line-item "Order Type" stayed stuck
    // on its add-to-cart value, showing stale info in the checkout summary;
    // (2) admin order detail showed duplicate info per item. Keeping them
    // ONLY at the cart-attribute level is the cleaner data model.
    var properties = {
      'Flavors': flavorSummary,
      // Hidden per-line stamp of the pickup date this box's flavors belong to.
      // The checkout Validation Function compares this against the cart's
      // "Pickup/Delivery Date" attribute and BLOCKS the order if they differ —
      // which is exactly what happens when a stale box from an earlier day
      // (whose flavors aren't on the chosen day's menu) lingers in the cart
      // alongside a box built for a later date. (Renee/Mike 2026-06-04, order
      // #1099.) Underscore prefix = hidden from the checkout/admin line display
      // — consistent with the data-model note above — but still readable by the
      // Function via `line.attribute(key:)`. Same source string as the cart
      // "Pickup/Delivery Date" attribute, so a single-date order always matches.
      '_Built For Date': state.dateStr
    };

    // Cupcake pick variant mapping — SIZE-AWARE (2026-04-24)
    // Shopify has one pick-bundle product per box size (4/6/12), each with five
    // design variants. DPL syncs these from Square. Picks aren't offered on 1-
    // or 2-count boxes (guarded in applyPickEligibility). Custom-box mode has
    // no matching bundle, so it falls through to the ID-not-found branch below
    // and no pick is added — fine, since the UI doesn't expose picks there in
    // practice beyond the empty visibility check.
    var pickVariantIds = {
      4: {
        'happy-birthday':  45955491791009,
        'happy-21st':      46628968824993,
        'happy-50th':      46628969152673,
        'thank-you':       45955491823777,
        'congratulations': 45955491856545,
        'its-a-girl':      45955491889313,
        'its-a-boy':       45955491922081
      },
      6: {
        'happy-birthday':  45958347423905,
        'happy-21st':      46628966236321,
        'happy-50th':      46628967121057,
        'thank-you':       45958347456673,
        'congratulations': 45958347489441,
        'its-a-girl':      45958347522209,
        'its-a-boy':       45958347554977
      },
      12: {
        'happy-birthday':  45955492642977,
        'happy-21st':      46628964925601,
        'happy-50th':      46628965318817,
        'thank-you':       45955492675745,
        'congratulations': 45955492708513,
        'its-a-girl':      45955492741281,
        'its-a-boy':       45955492774049
      }
    };

    var pickLabels = {
      'happy-birthday': 'Happy Birthday',
      'happy-21st': 'Happy 21st',
      'happy-50th': 'Happy 50th',
      'thank-you': 'Thank You',
      'congratulations': 'Graduation',
      'its-a-girl': "It's a Girl",
      'its-a-boy': "It's a Boy"
    };

    // Add cupcake pick label as line item property on the box
    if (state.selectedPick && state.selectedPick !== 'none') {
      properties['Cupcake Pick'] = pickLabels[state.selectedPick] || state.selectedPick;
    }

    // Add delivery-specific properties
    if (state.orderType === 'Delivery') {
      var deliveryNotes = getDeliveryNotes();
      if (deliveryNotes) {
        properties['Delivery Instructions'] = deliveryNotes;
      }
      var deliveryZip = sessionStorage.getItem('cc_delivery_zip') || '';
      if (deliveryZip) {
        properties['Delivery Zip'] = deliveryZip;
      }
    }

    // Build box cart item(s).
    // ── DECOMPOSITION (2026-04-27) ──────────────────────────────
    // Custom-amount orders are now decomposed into REAL box products
    // (1, 2, 4, 6, 12-count) using a greedy largest-first algorithm.
    // Example: 7 cupcakes → 1× 6-count box + 1× 1-count box.
    // Flavors are distributed across the boxes (largest box gets the
    // fullest mix). Each line item carries its own properties so
    // fulfillment (and the Square Flow downstream) sees real box SKUs
    // with their share of flavors. The 12-count box's per-cupcake
    // discount is automatically passed to the customer for any order
    // ≥12 since we use the actual 12 Cupcakes product.
    //
    // Standard-size orders (state.boxSize is 1, 2, 4, 6, or 12) skip
    // decomposition and go in as a single line item using the box
    // product the customer picked on step 3 — same as before.
    var boxItems;
    // Cart-level attributes for flavors — separate from line item properties.
    // These DO export to the CSV's `Note Attributes` column (line item
    // properties don't), so the /pages/bake-list parser can read them.
    // Keys are human-readable so they look reasonable in the admin order's
    // "Additional details" section.
    var flavorCartAttrs = {};
    if (state.isCustomBox) {
      // Greedy decompose total quantity into available standard box sizes.
      var BOX_SIZES_DESC = [12, 6, 4, 2, 1];
      function decomposeQty(qty) {
        var pieces = [];
        var remaining = qty;
        BOX_SIZES_DESC.forEach(function (size) {
          while (remaining >= size) {
            pieces.push(size);
            remaining -= size;
          }
        });
        return pieces; // e.g. 7 → [6, 1]; 25 → [12, 12, 1]
      }
      // Look up each box product (variant id) by its size.
      // For custom-box decomposition we PREFER the filler variants for
      // sizes 1/2/4 (untagged twins) so the cart line items don't pick up
      // Harrisonburg meals tax that should only apply to standalone
      // direct purchases of those small box products. The 6 and 12 boxes
      // are already untagged on the originals, so we use them as-is.
      function findBoxBySize(size) {
        var filler = fillerBoxes && fillerBoxes[String(size)];
        if (filler && filler.variantId) {
          return { id: filler.id, variantId: filler.variantId, title: filler.title, handle: filler.handle, price: filler.price, priceFormatted: filler.priceFormatted, maxQty: filler.maxQty };
        }
        return allBoxes.find(function (b) { return b.maxQty === size; });
      }
      // Distribute flavor selections across the decomposed boxes.
      // Largest box first, "pour" each flavor into it until full, spill
      // into the next. Returns array parallel to `pieces`, each element
      // being [{id, title, qty}, ...] — the slice of selections for that
      // box.
      function distributeFlavors(pieces, selections) {
        // Deep-copy selections so we can mutate quantities as we drain.
        var pool = selections.map(function (s) {
          return { flavorId: s.flavorId, title: s.title, qty: s.qty };
        });
        return pieces.map(function (size) {
          var bucket = [];
          var need = size;
          for (var i = 0; i < pool.length && need > 0; i++) {
            if (pool[i].qty <= 0) continue;
            var take = Math.min(pool[i].qty, need);
            bucket.push({ flavorId: pool[i].flavorId, title: pool[i].title, qty: take });
            pool[i].qty -= take;
            need -= take;
          }
          return bucket;
        });
      }

      var pieces = decomposeQty(state.totalSelected);
      var perBoxFlavors = distributeFlavors(pieces, state.selections);

      boxItems = pieces.map(function (size, idx) {
        var box = findBoxBySize(size);
        if (!box) {
          // Defensive: missing standard box product. Shouldn't happen, but
          // if it does we throw so the .catch surfaces a real error instead
          // of silently dropping cupcakes from the order.
          throw new Error('No Shopify product found for box size ' + size);
        }
        var thisBoxFlavors = perBoxFlavors[idx];
        var thisBoxFlavorSummary = thisBoxFlavors.map(function (f) {
          return f.qty + 'x ' + f.title;
        }).join(', ');
        // Per-line-item properties — copy shared properties (Order Type, etc.)
        // and override the flavor summary with this box's slice.
        var lineProps = Object.assign({}, properties, {
          'Flavors': thisBoxFlavorSummary
        });
        // Cart-level mirror keyed by box index + size for CSV export.
        flavorCartAttrs['Flavors - Box ' + (idx + 1) + ' (' + size + ' Cupcakes)'] = thisBoxFlavorSummary;
        return {
          id: box.variantId,
          quantity: 1,
          properties: lineProps
        };
      });
    } else {
      // Standard box: add box product × 1 (single line item, unchanged).
      boxItems = [{
        id: state.boxProduct.variantId,
        quantity: 1,
        properties: properties
      }];
      // Cart-level mirror for CSV export. Single-box orders only need one key.
      var stdSize = (state.boxProduct && state.boxProduct.maxQty) || state.totalSelected || state.boxSize;
      flavorCartAttrs['Flavors (' + stdSize + ' Cupcakes)'] = flavorSummary;
    }

    // Build pick cart item (separate request so box always succeeds).
    // Each pick-bundle product is a single line item priced for the full box
    // size (e.g. `6 Cupcake Picks — Happy Birthday` at $5 for all six picks),
    // so quantity is 1 — one bundle per order. If the current box size has
    // no matching bundle (custom box, 1/2 count), we skip the pick add.
    var pickItem = null;
    if (
      state.selectedPick && state.selectedPick !== 'none' &&
      pickVariantIds[state.boxSize] &&
      pickVariantIds[state.boxSize][state.selectedPick]
    ) {
      // The pick bundle is its own product (e.g. "12 Cupcake Picks — Happy
      // Birthday"), so the product title alone already communicates what it
      // is. No need for an extra `_pick_for_order` flag — nothing in the
      // codebase reads it, and it just clutters the admin view.
      pickItem = {
        id: pickVariantIds[state.boxSize][state.selectedPick],
        quantity: 1
      };
    }

    // Atomic add: box + pick bundle in a single cart request.
    // ────────────────────────────────────────────────────────────────────
    // Mike flagged 2026-05-14: a customer's order showed the "Cupcake Pick:
    // Happy Birthday" property on the box but the pick bundle was never
    // charged. Root cause: the previous code did two separate fetches —
    // box first, then pick — with the pick call wrapped in a silent
    // `.catch(err => console.warn(...))`. Any transient failure on the
    // second call (rate limit, publishing state, etc.) meant the customer
    // checked out with only the box. The label was already attached as a
    // line-item property in step 1, so to the customer the order LOOKED
    // correct but they paid nothing for the pick.
    //
    // The fix is atomic: bundle both line items into one /cart/add.js call.
    // Shopify either accepts both (charge succeeds) or rejects the whole
    // thing (user sees an error and retries) — no half-order state is
    // possible. The product team can investigate any specific failure mode
    // via the surfaced error, instead of a silent log buried in the console.
    var allItems = pickItem ? boxItems.concat([pickItem]) : boxItems;
    fetch(config.cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: allItems })
    })
    .then(function (res) {
      if (!res.ok) {
        // Surface Shopify's actual error text so we know whether it was the
        // box or the pick that failed. Helps debugging recurrences.
        return res.text().then(function (txt) {
          throw new Error('Cart add failed (' + res.status + '): ' + txt.slice(0, 200));
        });
      }
      return res.json();
    })
    .then(function () {
      // Step 3: Build cart attributes
      var attrs = {
        'Order Type': state.orderType,
        'Pickup/Delivery Date': state.dateStr,
        // Time is mandatory as of 2026-05-14 — the wizard now gates step 2's
        // Continue button until a slot is chosen, so this is always populated
        // by the time the user reaches checkout. Flows through to the admin
        // order page (Additional details) AND the order-confirmation email
        // (the brand-pink callout snippet renders from this attribute).
        'Pickup/Delivery Time': state.pickupTime
      };
      // Mirror flavors into cart attributes so they EXPORT to the CSV's
      // `Note Attributes` column. (Line item properties don't export to CSV,
      // but cart attributes do.) The /pages/bake-list parser reads these.
      Object.assign(attrs, flavorCartAttrs);
      // Delivery-specific attributes
      var deliveryNotesForOrder = '';
      if (state.orderType === 'Delivery') {
        deliveryNotesForOrder = getDeliveryNotes();
        if (deliveryNotesForOrder) attrs['Delivery Instructions'] = deliveryNotesForOrder;
        var zip = sessionStorage.getItem('cc_delivery_zip') || '';
        if (zip) attrs['Delivery Zip'] = zip;
      }

      // Cart update payload.
      // ALWAYS fetch the current cart first so we can:
      //   (a) Clear STALE flavor attributes left over from a prior box build.
      //       Flavor attrs are keyed by size ("Flavors (6 Cupcakes)" vs
      //       "Flavors (12 Cupcakes)"). `/cart/clear.js` empties line items but
      //       NOT cart attributes, so building a 6-box, clearing/restarting,
      //       then building a 12-box left BOTH keys on the final order — even
      //       though only the 12-box was a real line item. (Renee/Mike, order
      //       #1099, 2026-06-02; the orphaned 6-box even carried a wrong-day
      //       flavor.) Rule: a "Flavors*" attribute is stale when its value
      //       matches NO current box line item's "Flavors" property → set it to
      //       '' so Shopify drops it. Legitimate multi-box orders are safe:
      //       every box's value still matches a live line item, so nothing is
      //       cleared.
      //   (b) Merge delivery instructions with any pre-existing customer note
      //       (never overwrite — we previously lost real customer notes).
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : { items: [], attributes: {}, note: '' }; })
        .then(function (cart) {
          // Flavor summaries that legitimately belong on this cart: every
          // current box line item's "Flavors" property, plus the ones we're
          // writing right now (guards against a momentarily stale cart.js).
          var liveSummaries = (cart.items || []).map(function (it) {
            return it.properties && it.properties['Flavors'];
          }).filter(Boolean);
          Object.keys(flavorCartAttrs).forEach(function (k) { liveSummaries.push(flavorCartAttrs[k]); });
          var existingAttrs = cart.attributes || {};
          Object.keys(existingAttrs).forEach(function (k) {
            if (/^Flavors\b/.test(k) && !(k in flavorCartAttrs) && liveSummaries.indexOf(existingAttrs[k]) === -1) {
              attrs[k] = ''; // orphaned flavor set from a prior build → drop it
            }
          });

          var body = { attributes: attrs };
          if (deliveryNotesForOrder) {
            var existingNote = (cart && cart.note) || '';
            var deliveryLine = 'Delivery instructions: ' + deliveryNotesForOrder;
            // Don't duplicate if we already wrote the same delivery line in a
            // prior wizard run (re-building a second box, same instructions).
            var mergedNote;
            if (existingNote.indexOf(deliveryLine) !== -1) mergedNote = existingNote;
            else if (existingNote) mergedNote = deliveryLine + '\n\n' + existingNote;
            else mergedNote = deliveryLine;
            body.note = mergedNote;
          }
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });
        });
    })
    .then(function () {
      // Success!
      var successMsg;
      if (state.isCustomBox) {
        successMsg = state.totalSelected + ' cupcake' + (state.totalSelected !== 1 ? 's' : '') +
          ' with ' + state.selections.length + ' flavor' +
          (state.selections.length !== 1 ? 's' : '') + ' — ' + state.dateStr;
      } else {
        successMsg = state.boxProduct.title + ' with ' + state.selections.length + ' flavor' +
          (state.selections.length !== 1 ? 's' : '') + ' — ' + state.dateStr;
      }
      document.getElementById('ow-success-text').textContent = successMsg;
      showSuccess();

      // Update the theme's cart count in header
      updateThemeCartCount();

      // Dispatch event for cart drawer
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    })
    .catch(function (err) {
      console.error('Add to cart error:', err);
      btn.disabled = false;
      btn.textContent = 'Error — Try Again';
      setTimeout(function () { updateAddToCartBtn(); }, 2000);
    });
  }

  /* ═══════════════════════════════════════════════════════════
     CART COUNT UPDATE
     ═══════════════════════════════════════════════════════════ */
  function updateThemeCartCount() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Update theme-specific cart count elements
        var countEls = document.querySelectorAll(
          '.thb-cart-count, .cart-count, [data-cart-count], .header__cart-count'
        );
        countEls.forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) el.style.display = '';
        });

        // Also try the theme's built-in cart form count
        var formCount = document.querySelector('.thb-cart-form--count');
        if (formCount) formCount.textContent = '(' + cart.item_count + ')';
      })
      .catch(function () { /* silently fail */ });
  }

  /* ═══════════════════════════════════════════════════════════
     EVENT BINDINGS
     ═══════════════════════════════════════════════════════════ */
  function bindGlobalEvents() {
    // Back/Next buttons
    $wizard.querySelectorAll('[data-action="back"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goToStep(state.step - 1);
      });
    });

    $wizard.querySelectorAll('[data-action="next"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goToStep(state.step + 1);
      });
    });

    // Progress step clicks
    $progressSteps.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = parseInt(this.getAttribute('data-goto'));
        if (!this.disabled && target <= state.step) {
          goToStep(target);
        }
      });
    });

    // Cupcake pick selection
    $wizard.querySelectorAll('.ow-picks__option').forEach(function (btn) {
      btn.addEventListener('click', function () {
        $wizard.querySelectorAll('.ow-picks__option').forEach(function (b) { b.classList.remove('is-active'); });
        this.classList.add('is-active');
        state.selectedPick = this.getAttribute('data-pick');
        updateAddToCartBtn(); // Reflect pick cost in button total
      });
    });

    // Add to cart
    document.getElementById('ow-add-to-cart').addEventListener('click', handleAddToCart);

    // Build another
    var buildAnother = document.getElementById('ow-build-another');
    if (buildAnother) {
      buildAnother.addEventListener('click', function () {
        // Reset box selections but keep order type and date
        state.boxProduct = null;
        state.boxSize = 0;
        state.selections = [];
        state.totalSelected = 0;
        state.isCustomBox = false;
        state.selectedPick = 'none';
        // Reset pick UI
        $wizard.querySelectorAll('.ow-picks__option').forEach(function (b) { b.classList.remove('is-active'); });
        var noPick = $wizard.querySelector('[data-pick="none"]');
        if (noPick) noPick.classList.add('is-active');

        // Deselect any previously selected box card
        var boxCards = $wizard.querySelectorAll('.ow-box-card');
        boxCards.forEach(function (c) { c.classList.remove('is-selected'); });

        goToStep(3);
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════════════════ */
  function init() {
    initStep1();
    bindGlobalEvents();
    goToStep(1);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
// deploy-bump 1777065458

// force-push-marker-1777065478701536000
// merge-bump 1777065669352384000
// box-builder-cleanup-bump 1777299646
// big-bump-1 1777299747550498000
// big-bump-2 1777299747552737000
// big-bump-3 1777299747554552000
// big-bump-4 1777299747556540000
// big-bump-5 1777299747559432000
// fulfill-lock-bump 1777301634221538000
